import {useMutation, useQuery} from '@apollo/client';
import {Spin, Table, Tooltip} from 'antd';
import {GET_ALL_COLLECTION_GROUPS} from 'graphql/queries/collections';
import {toast} from 'react-toastify';
import ui from '../collectionsUI.module.scss';
import styles from './collectionGroups.module.scss';
import {Button} from '@dabafinance/react-components';
import {useNavigate} from 'react-router-dom';
import {useState} from 'react';
import DBModal from 'components/modal/modal';
import {DELETE_COLLECTION_GROUP} from 'graphql/mutations/collections';

/**
 * Collection groups.
 *
 * Restyled onto the shared collections design layer so the two tabs under
 * Collections read as one screen rather than two.
 */
const CollectionGroups = () => {
  const [modalContent, setModalContent] = useState({isOpen: false});
  const navigate = useNavigate();

  const {data, loading, refetch} = useQuery(GET_ALL_COLLECTION_GROUPS, {
    variables: {
      filters: {
        paginate: false,
      },
    },
    errorPolicy: 'all',
    onError: error => toast.error(error?.message),
  });
  const [deleteCollectionGroup, {loading: loadingDelete}] = useMutation(
    DELETE_COLLECTION_GROUP,
  );

  // Soft-deleted groups come back from this endpoint; they should not be
  // editable or linkable.
  const groups = (
    data?.getAllCollectionGroups?.data?.collectionGroups ?? []
  ).filter(group => !group?.isDeleted);

  const columns = [
    {
      key: 'title',
      title: 'Group',
      render: (text, record) => (
        <div className={ui.nameCell}>
          {record?.imageUrl ? (
            <img src={record.imageUrl} alt="" className={ui.nameIcon} />
          ) : (
            <div className={`${ui.nameIcon} ${ui.nameIconFallback}`}>
              {(record?.title ?? '?').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <span className={ui.cellName}>{record?.title}</span>
            <span className={ui.cellSub}>
              {/* Spelled out — never EN/FR. */}
              {record?.language === 'FR' ? 'French' : 'English'}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'collectionCount',
      title: 'Collections',
      width: 130,
      align: 'right',
      render: (text, record) => (
        <span className={ui.cellNum}>{record?.collectionCount ?? 0}</span>
      ),
    },
    {
      key: 'visible',
      title: 'Visibility',
      width: 130,
      render: (text, record) => (
        <span
          className={`${ui.chip} ${ui.chipDot} ${
            record?.isVisible ? ui.chipOk : ui.chipMute
          }`}>
          {record?.isVisible ? 'Visible' : 'Hidden'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 90,
      render: (text, record) => (
        <div className={ui.rowActions}>
          <Tooltip title="Edit group">
            <button
              type="button"
              className={ui.iconButton}
              onClick={() =>
                navigate(`/dashboard/collections/groups/update/${record?.id}`)
              }>
              <PencilIcon />
            </button>
          </Tooltip>
          <Tooltip title="Delete group">
            <button
              type="button"
              className={`${ui.iconButton} ${ui.iconButtonDanger}`}
              onClick={() =>
                setModalContent({
                  isOpen: true,
                  id: record?.id,
                  title: record?.title,
                })
              }>
              <TrashIcon />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleDelete = () => {
    deleteCollectionGroup({
      variables: {collectionGroupId: modalContent?.id},
    })
      .then(({data: result}) => {
        if (result?.deleteCollectionGroup?.__typename === 'Error')
          return toast.error(result.deleteCollectionGroup.message);
        toast.success(`${modalContent?.title ?? 'Group'} deleted`);
        refetch();
        setModalContent({isOpen: false});
      })
      .catch(error => toast.error(error?.message));
  };

  return (
    <div className={ui.panel}>
      <div className={ui.panelHead}>
        <div>
          <h3>Collection groups</h3>
          <span>
            Groups gather related collections together for investors to browse.
          </span>
        </div>
        <Button
          onClick={() => navigate('/dashboard/collections/groups/create')}
          label="Create group"
        />
      </div>

      {loading ? (
        <div className={ui.loading}>
          <Spin size="large" />
          <h1>Loading groups…</h1>
        </div>
      ) : groups.length === 0 ? (
        <div className={ui.empty}>
          <h3>No collection groups yet</h3>
          <p>
            A group is a browsable shelf of related collections. Create one to
            start organising them.
          </p>
          <Button
            onClick={() => navigate('/dashboard/collections/groups/create')}
            label="Create group"
          />
        </div>
      ) : (
        <div className={ui.table}>
          <Table
            dataSource={groups}
            columns={columns}
            rowKey={record => record?.id}
            pagination={groups.length > 10 ? {pageSize: 10} : false}
          />
        </div>
      )}

      <DBModal
        isOpen={modalContent.isOpen}
        handleClose={() => setModalContent({isOpen: false})}
        content={
          <div className={styles.modalRoot}>
            <div className={styles.modalTop}>
              <h1>Delete this group?</h1>
              <span>
                {modalContent?.title
                  ? `"${modalContent.title}" will no longer be shown to investors. The collections inside it are not deleted.`
                  : 'The collections inside it are not deleted.'}
              </span>
            </div>
            <div className={styles.buttons}>
              <Button
                disabled={loadingDelete}
                label="Delete"
                onClick={handleDelete}
              />
              <Button
                label="Cancel"
                type="secondary"
                onClick={() => setModalContent({isOpen: false})}
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

const PencilIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default CollectionGroups;
